interface WidgetInstance {
  id: string;
  agentId: string;
  isActive: boolean;
  isExpanded: boolean;
  onActivate?: () => void;
  onDeactivate?: () => void | Promise<void>;
  onExpand?: () => void;
  onCollapse?: () => void;
  trigger?: () => void;
}

interface WidgetManagerEventMap {
  "widget-activated": { widgetId: string };
  "widget-deactivated": { widgetId: string };
  "widget-expanded": { widgetId: string };
  "widget-collapsed": { widgetId: string };
}

class WidgetManager {
  private static instance: WidgetManager;
  private widgets: Map<string, WidgetInstance> = new Map();
  private activeWidgetId: string | null = null;
  private expandedWidgetId: string | null = null;
  private eventListeners: Map<keyof WidgetManagerEventMap, Function[]> =
    new Map();

  private constructor() {
    // Private constructor for singleton
  }

  static getInstance(): WidgetManager {
    if (!WidgetManager.instance) {
      WidgetManager.instance = new WidgetManager();
    }
    return WidgetManager.instance;
  }

  registerWidget(widget: WidgetInstance): void {
    this.widgets.set(widget.id, widget);
  }

  unregisterWidget(widgetId: string): void {
    if (this.activeWidgetId === widgetId) {
      this.deactivateWidget(widgetId);
    }
    if (this.expandedWidgetId === widgetId) {
      this.collapseWidget(widgetId);
    }
    this.widgets.delete(widgetId);
  }

  async activateWidget(widgetId: string): Promise<boolean> {
    const widget = this.widgets.get(widgetId);
    if (!widget) {
      console.warn(`Widget ${widgetId} not found`);
      return false;
    }

    // Deactivate current active widget if different
    if (this.activeWidgetId && this.activeWidgetId !== widgetId) {
      await this.deactivateWidget(this.activeWidgetId);
    }

    // Activate the new widget
    this.activeWidgetId = widgetId;
    widget.isActive = true;

    // Trigger widget-specific activation callback
    if (widget.onActivate) {
      widget.onActivate();
    }

    // Emit global event
    this.emit("widget-activated", { widgetId });

    // Update all widgets to reflect new state
    this.updateAllWidgets();

    return true;
  }

  async deactivateWidget(widgetId: string): Promise<boolean> {
    const widget = this.widgets.get(widgetId);
    if (!widget || !widget.isActive) {
      return false;
    }

    widget.isActive = false;

    // Trigger widget-specific deactivation callback and wait if it's async
    if (widget.onDeactivate) {
      await widget.onDeactivate();
    }

    // Clear active widget if this was it
    if (this.activeWidgetId === widgetId) {
      this.activeWidgetId = null;
    }

    // Emit global event
    this.emit("widget-deactivated", { widgetId });

    // Update all widgets to reflect new state
    this.updateAllWidgets();

    return true;
  }

  expandWidget(widgetId: string): boolean {
    const widget = this.widgets.get(widgetId);
    if (!widget) {
      console.warn(`Widget ${widgetId} not found`);
      return false;
    }

    // Collapse current expanded widget if different
    if (this.expandedWidgetId && this.expandedWidgetId !== widgetId) {
      this.collapseWidget(this.expandedWidgetId);
    }

    // Expand the new widget
    this.expandedWidgetId = widgetId;
    widget.isExpanded = true;

    // Trigger widget-specific expansion callback
    if (widget.onExpand) {
      widget.onExpand();
    }

    // Emit global event
    this.emit("widget-expanded", { widgetId });

    // Update all widgets to reflect new state
    this.updateAllWidgets();

    return true;
  }

  collapseWidget(widgetId: string): boolean {
    const widget = this.widgets.get(widgetId);
    if (!widget || !widget.isExpanded) {
      return false;
    }

    widget.isExpanded = false;

    // Trigger widget-specific collapse callback
    if (widget.onCollapse) {
      widget.onCollapse();
    }

    // Clear expanded widget if this was it
    if (this.expandedWidgetId === widgetId) {
      this.expandedWidgetId = null;
    }

    // Emit global event
    this.emit("widget-collapsed", { widgetId });

    // Update all widgets to reflect new state
    this.updateAllWidgets();

    return true;
  }

  isWidgetActive(widgetId: string): boolean {
    return this.activeWidgetId === widgetId;
  }

  isWidgetExpanded(widgetId: string): boolean {
    return this.expandedWidgetId === widgetId;
  }

  getActiveWidgetId(): string | null {
    return this.activeWidgetId;
  }

  getExpandedWidgetId(): string | null {
    return this.expandedWidgetId;
  }

  isAnyWidgetActive(): boolean {
    return this.activeWidgetId !== null;
  }

  isAnyWidgetExpanded(): boolean {
    return this.expandedWidgetId !== null;
  }

  canWidgetActivate(widgetId: string): boolean {
    // A widget can activate if:
    // 1. No other widget is active, OR
    // 2. This widget is already the active one
    return !this.activeWidgetId || this.activeWidgetId === widgetId;
  }

  async triggerWidgetAction(widgetId: string): Promise<void> {
    const widget = this.widgets.get(widgetId);
    if (widget && widget.trigger) {
      // If clicking the currently active widget, just trigger it (this will toggle/stop it)
      if (this.activeWidgetId === widgetId) {
        console.log(`Triggering currently active widget ${widgetId} (will stop conversation)`);
        widget.trigger();
        return;
      }
      
      // If another widget is active, deactivate it first
      if (this.activeWidgetId && this.activeWidgetId !== widgetId) {
        console.log(`Deactivating widget ${this.activeWidgetId} before activating ${widgetId}`);
        await this.deactivateWidget(this.activeWidgetId);
        // Add a small delay to ensure audio has stopped
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Trigger the new widget
      widget.trigger();
    } else {
      // Widget might not be registered yet due to React rendering timing
      // Retry after a short delay to allow useEffect to run
      console.warn(`Widget ${widgetId} not found, retrying in 100ms...`);
      setTimeout(async () => {
        const retryWidget = this.widgets.get(widgetId);
        if (retryWidget && retryWidget.trigger) {
          // If clicking the currently active widget, just trigger it (this will toggle/stop it)
          if (this.activeWidgetId === widgetId) {
            console.log(`Triggering currently active widget ${widgetId} (will stop conversation) (retry)`);
            retryWidget.trigger();
            return;
          }
          
          // If another widget is active, deactivate it first
          if (this.activeWidgetId && this.activeWidgetId !== widgetId) {
            console.log(`Deactivating widget ${this.activeWidgetId} before activating ${widgetId} (retry)`);
            await this.deactivateWidget(this.activeWidgetId);
            // Add a small delay to ensure audio has stopped
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          
          // Trigger the new widget
          retryWidget.trigger();
        } else {
          console.error(`Widget ${widgetId} or its trigger function not found after retry.`);
        }
      }, 100);
    }
  }

  private updateAllWidgets(): void {
    // Force re-render of all registered widgets by calling their callbacks
    this.widgets.forEach((widget, widgetId) => {
      // This will be handled by individual widget state updates
      // Each widget will listen to the manager's events
    });
  }

  // Event system for widgets to listen to state changes
  on<K extends keyof WidgetManagerEventMap>(
    event: K,
    callback: (data: WidgetManagerEventMap[K]) => void
  ): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }

  off<K extends keyof WidgetManagerEventMap>(
    event: K,
    callback: (data: WidgetManagerEventMap[K]) => void
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private emit<K extends keyof WidgetManagerEventMap>(
    event: K,
    data: WidgetManagerEventMap[K]
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((callback) => callback(data));
    }
  }

  // Utility method to get widget status for debugging
  getWidgetStatuses(): Array<{
    id: string;
    agentId: string;
    isActive: boolean;
    isExpanded: boolean;
  }> {
    return Array.from(this.widgets.values()).map((widget) => ({
      id: widget.id,
      agentId: widget.agentId,
      isActive: widget.isActive,
      isExpanded: widget.isExpanded,
    }));
  }
}

export default WidgetManager;
export type { WidgetInstance };
